import { cn } from '@/ui/lib/utils'

export interface QuantAnalystRailProps {
  symbol: string
  tf: string
  /** Names of the indicators switched on, in list order (what the chat would receive). */
  indicatorNames: string[]
  /** Live last price, or `null` before the candles arrive. */
  lastPrice: number | null
  /** The Daily pivots value while that indicator is on and live, else `null`. */
  pivot: number | null
  /** Price decimals for the pivot text. */
  digits: number
}

function Bubble({ me, who, children }: { me: boolean; who: string; children: string }) {
  return (
    <div className={cn('flex max-w-[88%] flex-col gap-1', me ? 'items-end self-end' : 'self-start')}>
      <span className="font-mono text-[9px] tracking-[0.16em] text-ink-50 uppercase">{who}</span>
      <div
        className={cn(
          'p-[9px_11px] font-mono text-[12.5px] leading-[1.55] [text-wrap:pretty]',
          me ? 'bg-ink text-paper' : 'border border-ink-20',
        )}
      >
        {children}
      </div>
    </div>
  )
}

/**
 * The Analyst rail body in its Planned layout (spec 036, handoff 4.6): context
 * chips, a canned thread, and a disabled composer. It makes no request. The
 * header (title, PLANNED tag, pin) lives in `QuantRail`.
 */
export function QuantAnalystRail({ symbol, tf, indicatorNames, lastPrice, pivot, digits }: QuantAnalystRailProps) {
  const pivotLine =
    pivot !== null && lastPrice !== null
      ? `Price is trading ${lastPrice > pivot ? 'above' : 'below'} the daily pivot at ${pivot.toFixed(digits)}. No sweep of the prior H4 swing yet, so the h4_sweep_ifvg setup is not armed.`
      : 'Turn on Daily pivots and I can place price against the pivot.'
  return (
    <>
      <div data-analyst-context className="flex shrink-0 flex-wrap gap-1 border-b border-ink-14 px-[18px] py-2.5">
        <span className="mr-1 self-center font-mono text-[9px] tracking-[0.16em] text-ink-50 uppercase">Context</span>
        {[symbol, tf, ...indicatorNames].map((chip, i) => (
          <span key={`${i}:${chip}`} className="border border-ink-20 px-[7px] py-0.5 font-mono text-[10.5px]">
            {chip}
          </span>
        ))}
      </div>
      <div data-analyst-thread tabIndex={0} className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-[18px] py-4">
        <Bubble me who="You">{`Is price reacting to the last FVG on ${symbol} ${tf}?`}</Bubble>
        <Bubble me={false} who="Analyst">
          {pivotLine}
        </Bubble>
        <Bubble me who="You">
          Mark the Asia low for me.
        </Bubble>
        <Bubble me={false} who="Analyst">
          Drawing on the chart is planned. For now I can only read what the indicator stream sends.
        </Bubble>
      </div>
      <div className="flex shrink-0 flex-col gap-2 border-t border-ink-20 px-[18px] pt-3 pb-4">
        <textarea
          disabled
          rows={3}
          aria-label="Message to the analyst"
          placeholder="Chat arrives in a later release"
          className="resize-none cursor-not-allowed border border-ink-20 bg-transparent px-2.5 py-[9px] font-mono text-[12.5px] text-ink outline-none"
        />
        <div className="flex items-center justify-between gap-2.5">
          <span className="font-mono text-[10px] text-ink-50">Reads the indicator stream for the pair on screen.</span>
          <button
            type="button"
            disabled
            className="cursor-not-allowed border-0 bg-ink px-4 py-2 font-sans text-[10.5px] font-bold tracking-[0.22em] text-paper uppercase opacity-40"
          >
            Send
          </button>
        </div>
      </div>
    </>
  )
}
